import store from '@ps/store'
import { Event, EventBus } from '@ps/utils/event-bus'
import { Message } from 'element-ui'
import { executeAction } from '@ps/workbench/undo-redo'
import { addChildAt } from '@ps/bridge/qc/node'
import { findByUuid } from '@ps/bridge/qc/node/utils'
import * as audioScript from '@ps/bridge/qc/scripts/audio'
import { addAudioNode, addAudioToPlayable } from '@ps/bridge/resource/audio'
import { libDataPool } from '@ps/bridge/resource/pool'
import _ from 'lodash'
import { wrapAddIvNode } from '@ps/store/interactive-video/brain/brain'

export default {
  namespaced: true,
  state: {
    audioBeh: null,
  },
  getters: {
    sourceAudioDuration: (state) =>
      (state.audioBeh && state.audioBeh.sourceAudioDuration) || 0,
    sourceAudioUrl: (state) =>
      (state.audioBeh && state.audioBeh.sourceAudioUrl) || '',
    audioUrl: (state) => (state.audioBeh && state.audioBeh.audioUrl) || '',
    clipStartTime: (state) =>
      (state.audioBeh && state.audioBeh.clipStartTime) || 0,
    clipEndTime: (state) => (state.audioBeh && state.audioBeh.clipEndTime) || 0,
    volume: (state) => (state.audioBeh && state.audioBeh.volume) || 100,
    autoPlay: (state) => !!(state.audioBeh && state.audioBeh.autoPlay),
    loopNumber: (state) => (state.audioBeh && state.audioBeh.loopNumber) || 1,
    loop: (state) => !!(state.audioBeh && state.audioBeh.loop),
    size: (state) => (state.audioBeh && state.audioBeh.size) || 0,
  },

  mutations: {
    getAudioBehavior(state, { node }) {
      const activeNode = node || store.state.workspace.game.activeNode
      state.audioBeh = audioScript.getScript(activeNode)
    },
    setAudioBehConfig(state, config) {
      const audioBeh = config.node
        ? audioScript.getScript(config.node, false)
        : state.audioBeh
      if (audioBeh) {
        audioBeh.setConfig(_.omit(config, ['node']))
      }
    },
  },
  actions: {
    async addAudioNode(
      { state, rootState, commit },
      { file_url, path, name, file_size, duration },
    ) {
      const parent = rootState.workspace.sceneManager.currentSceneNode
      const node = addAudioNode({ parent, name })

      // 记录到资源映射池，方便后续使用
      libDataPool.setLibData(path, {
        name: name,
        data: {
          file_url,
          path,
          file_size,
          duration,
        },
      })

      commit('getAudioBehavior', { node })
      commit('setAudioBehConfig', {
        sourceAudioUrl: file_url,
        audioUrl: path,
        size: file_size,
        sourceAudioDuration: duration,
        clipStartTime: 0,
        clipEndTime: duration,
      })

      // 互动视频编辑器插入节点处理
      const ivRes = await wrapAddIvNode(parent, node, 0)

      executeAction({
        action: {
          execute: ({ newValue }) => {
            const { node, parent, realNode } = newValue
            // 新增节点的时候在 recordHistory 之前已经增加节点了，所以需要判断节点树上是否有该节点
            if (!findByUuid(node.uuid)) {
              addChildAt({
                node,
                parent,
              })
            }
            store.dispatch('workspace/game/updateActiveNode', {
              node: realNode || node,
              record: false,
            })
          },
          undo: ({ oldValue }) => {
            const { node, activeUuid } = oldValue
            node.removeSelf()
            // 如果undo的时候记录了要激活的节点，则激活该节点
            const activeNode = findByUuid(activeUuid)
            if (activeNode) {
              store.dispatch('workspace/game/updateActiveNode', {
                node: activeNode,
                record: false,
              })
            } else {
              store.dispatch('workspace/game/clearActiveNode', {
                record: false,
              })
            }
          },
          value: {
            newValue: {
              node: ivRes ? ivRes.ivGroupNode : node,
              realNode: node,
              parent,
            },
            oldValue: {
              node: ivRes ? ivRes.ivGroupNode : node,
              activeUuid: store.state.workspace.game.activeNode
                ? store.state.workspace.game.activeNode.uuid
                : '',
            },
          },
          methodName: 'addAudioNodeAction',
        },
      })
      EventBus.$emit(Event.ADD_GAME_OBJECT, node.uuid)
    },

    async toggleAudioNode(
      { state, commit },
      { file_url, path, name, file_size, duration, node, record = true },
    ) {
      const nodes = Array.isArray(node)
        ? node
        : [node || store.state.workspace.game.activeNode]
      const data = {
        sourceAudioUrl: file_url,
        audioUrl: path,
        size: file_size,
        sourceAudioDuration: duration,
        volume: 100,
        clipStartTime: 0,
        clipEndTime: duration,
      }

      // 记录到资源映射池，方便后续使用
      libDataPool.setLibData(path, {
        name: name,
        data: {
          file_url,
          path,
          file_size,
          duration,
        },
      })

      const update = (data) => {
        commit('setAudioBehConfig', data)
      }
      const newValueArr = nodes.map((node) => {
        return {
          ...data,
          node,
        }
      })
      const oldValueArr = nodes.map((node) => {
        const audioBeh = audioScript.getScript(node, false)
        return {
          ...data,
          node,
          sourceAudioUrl: audioBeh ? audioBeh.sourceAudioUrl : '',
          audioUrl: audioBeh ? audioBeh.audioUrl : '',
          size: audioBeh ? audioBeh.size : 0,
          sourceAudioDuration: audioBeh ? audioBeh.sourceAudioDuration : 0,
          volume: audioBeh ? audioBeh.volume : 0,
        }
      })
      await executeAction({
        action: {
          execute: ({ newValue }) => {
            newValue.forEach((value) => {
              update(value)
            })
          },
          undo: ({ oldValue }) => {
            oldValue.forEach((item) => {
              update(item)
            })
          },
          value: {
            newValue: newValueArr,
            oldValue: oldValueArr,
          },
          methodName: 'toggleAudioNodeAction',
        },
        record,
      })
    },

    // eslint-disable-next-line consistent-return
    async addAudioResourceToProject(
      { rootState },
      { pid, asset_id, path = '', from },
    ) {
      try {
        return addAudioToPlayable({
          pid: Number(pid || rootState.workspace.pid),
          asset_id: String(asset_id),
          path,
          from,
        })
      } catch (err) {
        Message({
          message: err.msg,
          type: 'error',
        })
      }
    },
  },
}
